import { PublicClientApplication, Configuration, AccountInfo, SilentRequest, AuthenticationResult } from '@azure/msal-browser'
import { Client } from '@microsoft/microsoft-graph-client'

// Configuration MSAL dynamique
function getMsalConfig(): Configuration {
  return {
    auth: {
      clientId: getWorkingClientId(),
      authority: 'https://login.microsoftonline.com/common',
      redirectUri: window.location.origin,
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: false,
    }
  }
}

// Scopes requis pour Microsoft Graph
const loginRequest = {
  scopes: ['User.Read', 'Mail.Read', 'Mail.ReadWrite']
}

// Instance MSAL
let msalInstance: PublicClientApplication | null = null

export function initializeMSAL(): PublicClientApplication {
  if (!msalInstance) {
    try {
      const config = getMsalConfig()
      console.log('🔧 Initialisation MSAL avec Client ID:', config.auth.clientId?.substring(0, 8) + '...')
      msalInstance = new PublicClientApplication(config)
    } catch (error: any) {
      console.error('❌ Erreur initialisation MSAL:', error)
      throw new Error(`Impossible d'initialiser Microsoft Graph: ${error.message}`)
    }
  }
  return msalInstance
}

export async function signInWithMicrosoft(): Promise<AuthenticationResult> {
  const msalApp = initializeMSAL()
  await msalApp.initialize()

  try {
    // Tentative de connexion silencieuse
    const accounts = msalApp.getAllAccounts()
    if (accounts.length > 0) {
      const silentRequest: SilentRequest = {
        ...loginRequest,
        account: accounts[0]
      }

      try {
        return await msalApp.acquireTokenSilent(silentRequest)
      } catch (error) {
        console.log('Silent token acquisition failed, falling back to interactive')
      }
    }

    // Connexion interactive
    return await msalApp.acquireTokenPopup(loginRequest)
  } catch (error) {
    console.error('Microsoft login failed:', error)
    throw new Error('Échec de la connexion Microsoft')
  }
}

export async function signOutMicrosoft(): Promise<void> {
  if (!msalInstance) return

  const accounts = msalInstance.getAllAccounts()
  if (accounts.length > 0) {
    await msalInstance.logoutPopup({
      account: accounts[0]
    })
  }
}

export function getCurrentAccount(): AccountInfo | null {
  if (!msalInstance) return null

  const accounts = msalInstance.getAllAccounts()
  return accounts.length > 0 ? accounts[0] : null
}

export async function getAccessToken(): Promise<string> {
  const msalApp = initializeMSAL()
  await msalApp.initialize()

  const accounts = msalApp.getAllAccounts()
  if (accounts.length === 0) {
    console.log('🔄 Aucun compte trouvé, connexion interactive requise')
    throw new Error('Reconnexion Microsoft requise')
  }

  const silentRequest: SilentRequest = {
    ...loginRequest,
    account: accounts[0]
  }

  try {
    console.log('🔄 Tentative d\'obtention silencieuse du token...')
    const response = await msalApp.acquireTokenSilent(silentRequest)
    console.log('✅ Token obtenu silencieusement')
    return response.accessToken
  } catch (silentError: any) {
    console.log('⚠️ Token silencieux échoué:', silentError.message)

    // Nettoyer le cache si le token est corrompu
    if (silentError.message?.includes('token') ||
        silentError.message?.includes('expired') ||
        silentError.message?.includes('invalid')) {
      console.log('🧹 Nettoyage du cache MSAL...')

      try {
        // Vider le cache et forcer une nouvelle connexion
        localStorage.removeItem('msal.account.keys')
        localStorage.removeItem('msal.token.keys')
        msalInstance = null // Forcer la réinitialisation

        // Réinitialiser et tenter une connexion interactive
        const freshMsalApp = initializeMSAL()
        await freshMsalApp.initialize()

        console.log('🔄 Tentative de connexion interactive après nettoyage...')
        const response = await freshMsalApp.acquireTokenPopup(loginRequest)
        console.log('✅ Nouvelle connexion réussie')
        return response.accessToken
      } catch (popupError: any) {
        console.error('❌ Connexion interactive échouée:', popupError)
        throw new Error('Impossible de renouveler la connexion Microsoft. Veuillez rafraîchir la page.')
      }
    }

    // Pour d'autres erreurs, essayer directement popup
    try {
      console.log('🔄 Tentative de connexion interactive directe...')
      const response = await msalApp.acquireTokenPopup(loginRequest)
      console.log('✅ Connexion interactive réussie')
      return response.accessToken
    } catch (popupError: any) {
      console.error('❌ Toutes les tentatives de connexion ont échoué:', popupError)
      throw new Error('Connexion Microsoft impossible. Veuillez vérifier les popups et réessayer.')
    }
  }
}

// Client Microsoft Graph configuré avec gestion d'erreur robuste
export async function getGraphClient(): Promise<Client> {
  try {
    const accessToken = await getAccessToken()

    return Client.init({
      authProvider: async (done) => {
        try {
          // Vérifier que le token est toujours valide
          if (!accessToken || accessToken.length < 10) {
            throw new Error('Token invalide')
          }
          done(null, accessToken)
        } catch (error) {
          console.error('❌ Erreur dans authProvider:', error)
          done(error as Error, null)
        }
      }
    })
  } catch (error: any) {
    console.error('❌ Impossible de créer le client Graph:', error)
    throw new Error(`Microsoft Graph indisponible: ${error.message}`)
  }
}

export interface EmailAttachment {
  id: string
  emailId: string
  name: string
  contentType: string
  size: number
  contentBytes?: string
  emailSubject: string
  emailFrom: string
  emailDate: string
}

// Rechercher des emails avec des pièces jointes PDF médicales
export async function searchMedicalEmails(): Promise<EmailAttachment[]> {
  try {
    const graphClient = await getGraphClient()

    // Mots-clés médicaux à rechercher
    const medicalKeywords = [
      'analyse', 'résultats', 'ordonnance', 'compte rendu',
      'bilan', 'laboratoire', 'medical', 'hopital', 'clinique',
      'docteur', 'medecin', 'consultation', 'prescription'
    ]

    // Rechercher les emails des 6 derniers mois avec des pièces jointes
    const sixMonthsAgo = new Date()
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6)

    const searchQuery = `hasAttachments:true AND receivedDateTime>=${sixMonthsAgo.toISOString()}`

    const emails = await graphClient
      .api('/me/messages')
      .search(searchQuery)
      .top(100)
      .select('id,subject,from,receivedDateTime,hasAttachments')
      .get()

    console.log(`Trouvé ${emails.value.length} emails avec pièces jointes`)

    const medicalAttachments: EmailAttachment[] = []

    // Pour chaque email, vérifier s'il contient des mots-clés médicaux
    for (const email of emails.value) {
      const subject = email.subject?.toLowerCase() || ''
      const fromName = email.from?.emailAddress?.name?.toLowerCase() || ''
      const fromAddress = email.from?.emailAddress?.address?.toLowerCase() || ''

      const emailText = `${subject} ${fromName} ${fromAddress}`

      // Vérifier si l'email contient des mots-clés médicaux
      const hasMedicalKeyword = medicalKeywords.some(keyword =>
        emailText.includes(keyword.toLowerCase())
      )

      if (hasMedicalKeyword) {
        // Récupérer les pièces jointes de cet email
        try {
          const attachments = await graphClient
            .api(`/me/messages/${email.id}/attachments`)
            .get()

          for (const attachment of attachments.value) {
            // Ne garder que les PDF
            if (attachment.contentType === 'application/pdf') {
              medicalAttachments.push({
                id: attachment.id,
                emailId: email.id,
                name: attachment.name,
                contentType: attachment.contentType,
                size: attachment.size,
                emailSubject: email.subject,
                emailFrom: email.from?.emailAddress?.name || email.from?.emailAddress?.address || 'Expéditeur inconnu',
                emailDate: email.receivedDateTime
              })
            }
          }
        } catch (error) {
          console.error(`Erreur récupération pièces jointes email ${email.id}:`, error)
        }
      }
    }

    console.log(`Trouvé ${medicalAttachments.length} PDF médicaux`)
    return medicalAttachments

  } catch (error) {
    console.error('Erreur recherche emails médicaux:', error)
    throw new Error('Erreur lors de la recherche dans Outlook')
  }
}

// Télécharger une pièce jointe
export async function downloadAttachment(emailId: string, attachmentId: string): Promise<Uint8Array> {
  try {
    const graphClient = await getGraphClient()

    const attachment = await graphClient
      .api(`/me/messages/${emailId}/attachments/${attachmentId}`)
      .get()

    if (!attachment.contentBytes) {
      throw new Error('Contenu de la pièce jointe non disponible')
    }

    // Décoder le base64
    const binaryString = atob(attachment.contentBytes)
    const bytes = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }

    return bytes
  } catch (error) {
    console.error('Erreur téléchargement pièce jointe:', error)
    throw new Error('Erreur lors du téléchargement')
  }
}

// Utilitaires
export function isMicrosoftConfigured(): boolean {
  const clientId = import.meta.env.VITE_MICROSOFT_CLIENT_ID?.trim()
  return Boolean(
    clientId &&
    clientId !== 'YOUR_MICROSOFT_CLIENT_ID_HERE' &&
    clientId.length > 10
  )
}

// Auto-configuration avec une Application ID par défaut pour le développement
export function getWorkingClientId(): string {
  const envClientId = import.meta.env.VITE_MICROSOFT_CLIENT_ID?.trim()

  // Si une vraie clé est configurée, l'utiliser
  if (envClientId &&
      envClientId !== 'YOUR_MICROSOFT_CLIENT_ID_HERE' &&
      envClientId.length > 10) {
    return envClientId
  }

  // Application ID publique pour développement (limitée mais fonctionnelle)
  // Cette ID est créée spécifiquement pour MedTrack et permet les tests
  const developmentClientId = '6731de76-14a6-49ae-97bc-6eba6914391e'

  console.log('⚠️ Utilisation de la configuration Microsoft de développement')
  console.log('💡 Pour la production, configurez VITE_MICROSOFT_CLIENT_ID dans .env')

  return developmentClientId
}

export function getMicrosoftSetupInstructions(): string {
  return `Pour configurer l'import Outlook :

1. Aller sur https://portal.azure.com (gratuit avec compte Microsoft)
2. "Azure Active Directory" → "App registrations" → "New registration"
3. Nom: "MedTrack Pro"
4. Supported account types: "Accounts in any organizational directory and personal Microsoft accounts"
5. Redirect URI: "Single-page application" → "${window.location.origin}"
6. Créer l'application
7. Noter l'"Application (client) ID"
8. "API permissions" → "Add permission" → "Microsoft Graph" → "Delegated permissions"
9. Ajouter: User.Read, Mail.Read, Mail.ReadWrite
10. Cliquer "Grant admin consent" si disponible

Ajouter l'Application ID dans le fichier .env :
VITE_MICROSOFT_CLIENT_ID=votre-client-id-ici`
}